/**
 * @deprecated ABDA UI Plugin is deprecated as of November 28, 2025.
 * This plugin is a legacy subsystem - do not add new features.
 * See /docs/ABDA-Lessons-Learned.md for forensics analysis.
 * Replacement: AppFrame/SceneStream App Surface (Phase 2)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAbda } from './useAbda';
import { SessionToolbar } from './SessionToolbar';
import { AlertCircle, ExternalLink, X } from 'lucide-react';
import { HiveCoreScenePanel } from '../../components/HiveCore/HiveCoreScenePanel';
import { useHiveCoreOptional } from '../../services/hivecore-scene-stream/useHiveCoreOptional';

interface ABDAViewportProps {
  className?: string;
  autoStart?: boolean;
  initialUrl?: string;
}

interface UITab {
  id: string;
  label: string;
  type: 'control' | 'browser';
  url?: string;
}

export function ABDAViewport({ className = '', autoStart = false, initialUrl }: ABDAViewportProps) {
  const hiveCoreContext = useHiveCoreOptional();
  
  const {
    config,
    session,
    isLoading,
    error,
    currentUrl,
    isConnected,
    startSession,
    stopSession,
    navigateTo,
    checkPolicy,
    takeScreenshot,
  } = useAbda({ 
    autoConnect: true
  });

  const [renderMode, setRenderMode] = useState<'iframe' | 'remote' | 'blocked'>('iframe');
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);
  const [targetUrl, setTargetUrl] = useState<string | null>(initialUrl || null);
  
  const [tabs, setTabs] = useState<UITab[]>([
    { id: 'control', label: 'HiveCore Control', type: 'control' }
  ]);
  const [activeTabId, setActiveTabId] = useState<string>('control');

  useEffect(() => {
    if (autoStart && config && !session) {
      handleStart();
    }
  }, [autoStart, config, session]);

  useEffect(() => {
    if (initialUrl && session && !currentUrl) {
      handleNavigate(initialUrl);
    }
  }, [initialUrl, session, currentUrl]);

  const handleStart = async () => {
    try {
      await startSession();
      if (config?.homeUrl) {
        setTargetUrl(config.homeUrl);
      }
    } catch (err) {
      console.error('Failed to start session:', err);
    }
  };

  const handleStop = async () => {
    try {
      await stopSession();
      setTargetUrl(null);
      setScreenshotUrl(null);
      setRenderMode('iframe');
      setIframeError(false);
    } catch (err) {
      console.error('Failed to stop session:', err);
    }
  };

  const handleNavigate = async (url: string) => {
    try {
      setIframeError(false);
      setTargetUrl(url);

      const policyCheck = await checkPolicy(url);
      
      if (!policyCheck.isAllowed) {
        setRenderMode('blocked');
        return;
      }

      if (policyCheck.canEmbed) {
        setRenderMode('iframe');
        await navigateTo(url);
      } else {
        setRenderMode('remote');
        await navigateTo(url);
        await updateScreenshot();
      }
    } catch (err) {
      console.error('Failed to navigate:', err);
      setRenderMode('remote');
      await updateScreenshot();
    }
  };

  const updateScreenshot = async () => {
    try {
      const blob = await takeScreenshot();
      const url = URL.createObjectURL(blob);
      setScreenshotUrl(url);
    } catch (err) {
      console.error('Failed to take screenshot:', err);
    }
  };

  const handleScreenshot = async () => {
    try {
      const blob = await takeScreenshot();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `abda-screenshot-${Date.now()}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download screenshot:', err);
    }
  };

  const handleRefresh = () => {
    if (currentUrl) {
      handleNavigate(currentUrl);
    }
  };

  const handleOpenExternal = () => {
    if (currentUrl) {
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleIframeError = () => {
    setIframeError(true);
    setRenderMode('remote');
    if (currentUrl) {
      updateScreenshot();
    }
  };

  const handleAddTab = (url: string, label?: string) => {
    const newTab: UITab = {
      id: `tab-${Date.now()}`,
      label: label || new URL(url).hostname,
      type: 'browser',
      url
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
    handleNavigate(url);
  };

  const handleRemoveTab = (tabId: string) => {
    if (tabId === 'control') return;
    
    setTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);
      if (activeTabId === tabId && filtered.length > 0) {
        setActiveTabId(filtered[0].id);
      }
      return filtered;
    });
  };

  const handleTabSwitch = (tabId: string) => {
    setActiveTabId(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (tab?.type === 'browser' && tab.url) {
      handleNavigate(tab.url);
    }
  };

  const activeTab = tabs.find(t => t.id === activeTabId);

  useEffect(() => {
    if (renderMode === 'remote' && currentUrl) {
      const interval = setInterval(() => {
        updateScreenshot();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [renderMode, currentUrl]);

  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      <SessionToolbar
        isSessionActive={!!session}
        currentUrl={currentUrl}
        isLoading={isLoading}
        onStart={handleStart}
        onStop={handleStop}
        onNavigate={handleNavigate}
        onScreenshot={handleScreenshot}
        onRefresh={handleRefresh}
        onOpenExternal={handleOpenExternal}
      />

      <div className="flex-1 relative overflow-hidden">
        {error && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {activeTab?.type === 'control' && hiveCoreContext && (
          <div className="h-full overflow-auto">
            <HiveCoreScenePanel />
          </div>
        )}

        {!session && activeTab?.type === 'browser' && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <img 
                src="/beehive-logo-transparent.png" 
                alt="BeeHive Logo" 
                className="w-32 h-32 mx-auto mb-4 object-contain"
              />
              <h2 className="text-2xl font-bold text-white mb-2">ABDA Browser</h2>
              <p className="text-gray-400 mb-6">Initializing browser session...</p>
            </div>
          </div>
        )}

        {session && activeTab?.type === 'browser' && renderMode === 'iframe' && !iframeError && targetUrl && (
          <iframe
            src={targetUrl}
            className="w-full h-full border-0"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            onError={handleIframeError}
            title="ABDA Browser Viewport"
          />
        )}

        {session && activeTab?.type === 'browser' && renderMode === 'remote' && (
          <div className="flex flex-col items-center justify-center h-full bg-gray-800 p-4">
            <div className="max-w-4xl w-full">
              <div className="bg-yellow-900/50 border border-yellow-600 rounded-md p-4 mb-4 flex items-start gap-3">
                <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="text-yellow-500 font-semibold mb-1">Remote Render Mode</h3>
                  <p className="text-gray-300 text-sm">
                    This site blocks iframe embedding. Showing live screenshot (1 fps).
                  </p>
                </div>
              </div>

              {screenshotUrl ? (
                <div className="relative">
                  <img
                    src={screenshotUrl}
                    alt="Browser Screenshot"
                    className="w-full rounded-md shadow-lg"
                  />
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={handleOpenExternal}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-lg transition-colors"
                    >
                      <ExternalLink size={16} />
                      <span>Open External</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-900 rounded-md">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-600 border-t-blue-500" />
                </div>
              )}
            </div>
          </div>
        )}

        {session && activeTab?.type === 'browser' && renderMode === 'blocked' && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <AlertCircle className="mx-auto text-red-500 mb-4" size={64} />
              <h2 className="text-2xl font-bold text-white mb-2">URL Not Allowed</h2>
              <p className="text-gray-400 mb-4">
                This URL is not in the allowlist for your tenant.
              </p>
              {targetUrl && (
                <p className="text-gray-500 text-sm mb-6 break-all">
                  {targetUrl}
                </p>
              )}
              <button
                onClick={handleOpenExternal}
                className="flex items-center gap-2 mx-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                <ExternalLink size={16} />
                <span>Open in New Tab</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-700 bg-gray-900">
        <div className="flex items-center gap-2 overflow-x-auto p-2">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-t-md transition-colors ${
                activeTabId === tab.id
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-750 hover:text-gray-300'
              }`}
            >
              <button
                onClick={() => handleTabSwitch(tab.id)}
                className="flex items-center gap-2 text-sm whitespace-nowrap"
              >
                <span>{tab.label}</span>
              </button>
              {tab.id !== 'control' && (
                <button
                  onClick={() => handleRemoveTab(tab.id)}
                  className="ml-1 hover:text-red-400 transition-colors"
                  title="Close tab"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          
          {config && config.allowlist.length > 0 && (
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-700">
              <span className="text-gray-500 text-xs whitespace-nowrap">Add UI:</span>
              {config.allowlist.slice(0, 4).map((entry, index) => (
                <button
                  key={index}
                  onClick={() => handleAddTab(entry.url, entry.label)}
                  disabled={isLoading}
                  className="flex items-center gap-1 px-2 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-400 hover:text-white rounded-md transition-colors whitespace-nowrap text-xs"
                  title={`Add ${entry.label}`}
                >
                  {entry.icon && <span>{entry.icon}</span>}
                  <span>{entry.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ABDAViewport;
